import { inject, Injectable } from '@angular/core';
import {
  ContratCreateAndSignGQL,
  DemandeAffiliation as DemandeAffiliationGQL,
  DemandeValidationPj as DemandeValidationPjGQL,
  DemandeValidationPjContAgentCoEnvoyerEnSignatureGQL,
  DemandeHabilitation as DemandeHabilitationGQL,
  DocumentConformiteUpdateInput,
  DatesDebutValiditeValideesByDemandeValidationPjGQL,
  FormationContinueHeuresByDemandeValidationPjGQL,
  FormationContinueSummaryGQL,
  FormationContinueHeuresUpsertGQL,
  GetDemandeAffiliationForControlGQL,
  GetDemandeHabilitationForControlGQL,
  GetDemandeValidationPjForControlGQL,
  InformationsCarteTUpdateCommandInput,
  InformationsCoordonneesUpdateCommandInput,
  InformationsIdentiteUpdateCommandInput,
  InformationsProfessionLiberaleUpdateCommandInput,
  InformationsSocieteUpdateCommandInput,
  InformationsUpdateAttVigCommandInput,
  InformationsUpdateCerfaCommandInput,
  InformationsUpdateKbisCommandInput,
  InformationsUpdateNotifAffCommandInput,
  InformationsUpdateNotifDelaiUrssafCommandInput,
  InformationsUpdateRcpCommandInput,
  InformationsUpdateRsacCommandInput,
  InformationsUpdateSyntUrssafCommandInput,
  NonConformitesEnvoiGQL,
  NonConformitesValidationPjEnvoiGQL,
  NonConformiteUpdateInput,
  UpdateDocumentsConformiteGQL,
  UpdateDocumentsConformiteValidationPjGQL,
  UpdateInformationsAttVigGQL,
  UpdateInformationsCarteTGQL,
  UpdateInformationsCerfaGQL,
  UpdateInformationsCoordonneesGQL,
  UpdateInformationsIdentiteGQL,
  UpdateInformationsKbisGQL,
  UpdateInformationsNotifAffGQL,
  UpdateInformationsNotifDelaiUrssafGQL,
  UpdateInformationsProfessionLiberaleGQL,
  UpdateInformationsRcpGQL,
  UpdateInformationsRsacGQL,
  UpdateInformationsSocieteGQL,
  UpdateInformationsSyntUrssafGQL,
  ValidationDemandePjGQL,
} from '@graphql/generated';
import { CarteTPatch } from '@lib/components/controle-documents/models/carte-t-patch.model';
import { CoordonneesPatch } from '@lib/components/controle-documents/models/coordonnees-patch.model';
import { IdentitePatch } from '@lib/components/controle-documents/models/identite-patch.model';
import { StatutJuridiqueProfessionLiberalePatch } from '@lib/components/controle-documents/models/statut-juridique-profession-liberale-patch';
import { StatutJuridiqueSocietePatch } from '@lib/components/controle-documents/models/statut-juridique-societe-patch.model';
import { ControleDocumentsService } from '@lib/components/controle-documents/services/controle-documents.service';
import { DocumentModel } from '@lib/ged/document.model';
import { CiviliteMapper } from '@lib/mappers/graphql/civilite-mapper';
import { DemandeAffiliationMapper } from '@lib/mappers/graphql/demande-affiliation-mapper';
import { DemandeHabilitationMapper } from '@lib/mappers/graphql/demande-habilitation-mapper';
import { DemandeValidationPjMapper } from '@lib/mappers/graphql/demande-validation-pj-mapper';
import { DocumentStatutMapper } from '@lib/mappers/graphql/document-statut-mapper';
import { FormationContinueMapper } from '@lib/mappers/graphql/formation-continue-mapper';
import { NonConformiteCategorieMapper } from '@lib/mappers/graphql/non-conformite-categorie-mapper';
import { DemandeAffiliation } from '@lib/models/demande-affiliation/demande-affiliation.model';
import { DemandeHabilitation } from '@lib/models/demande-habilitation/demande-habilitation.model';
import { DemandeValidationPj } from '@lib/models/demande-validation-pj/demande-validation-pj.model';
import { FormationContinueSummary } from '@lib/models/formation-continue/formation-continue.model';
import { PieceJustificativeAttVig } from '@lib/models/demande-validation-pj/piece-justificative-att-vig.model';
import { PieceJustificativeCerfa } from '@lib/models/demande-validation-pj/piece-justificative-cerfa.model';
import { PieceJustificativeKbis } from '@lib/models/demande-validation-pj/piece-justificative-kbis.model';
import { PieceJustificativeNotifAff } from '@lib/models/demande-validation-pj/piece-justificative-notif-aff.model';
import { PieceJustificativeNotifDelaiUrssaf } from '@lib/models/demande-validation-pj/piece-justificative-notif-delai-urssaf.model';
import { PieceJustificativeRcpPatch } from '@lib/models/demande-validation-pj/piece-justificative-rcp.model';
import { PieceJustificativeRsac } from '@lib/models/demande-validation-pj/piece-justificative-rsac.model';
import { PieceJustificativeSyntUrssaf } from '@lib/models/demande-validation-pj/piece-justificative-synt-urssaf.model';
import { DemandeType } from '@lib/models/non-conformite/demande-type.model';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DemandeControleService extends ControleDocumentsService {
  private readonly getDemandeAffiliationGql = inject(GetDemandeAffiliationForControlGQL);
  private readonly updateInformationsIdentiteGql = inject(UpdateInformationsIdentiteGQL);
  private readonly updateInformationsCoordonneesGql = inject(
    UpdateInformationsCoordonneesGQL
  );
  private readonly updateInformationsCarteTGql = inject(UpdateInformationsCarteTGQL);
  private readonly updateInformationsProfessionLiberaleGql = inject(
    UpdateInformationsProfessionLiberaleGQL
  );
  private readonly updateInformationsSocieteGql = inject(UpdateInformationsSocieteGQL);
  private readonly updateDocumentsConformiteGql = inject(UpdateDocumentsConformiteGQL);
  private readonly nonConformitesEnvoiGql = inject(NonConformitesEnvoiGQL);
  private readonly contratCreateAndSignGql = inject(ContratCreateAndSignGQL);

  // Validation Pj
  private readonly getDemandeValidationPjGql = inject(
    GetDemandeValidationPjForControlGQL
  );
  private readonly nonConformitesValidationPjEnvoiGql = inject(
    NonConformitesValidationPjEnvoiGQL
  );
  private readonly updateDocumentsConformiteValidationPjGql = inject(
    UpdateDocumentsConformiteValidationPjGQL
  );
  private readonly validationDemandePjGql = inject(ValidationDemandePjGQL);
  private readonly demandeValidationPjContAgentCoEnvoyerEnSignatureGql = inject(
    DemandeValidationPjContAgentCoEnvoyerEnSignatureGQL
  );
  private readonly formationContinueHeuresByDemandeValidationPjGql = inject(
    FormationContinueHeuresByDemandeValidationPjGQL
  );
  private readonly formationContinueSummaryGql = inject(FormationContinueSummaryGQL);
  private readonly formationContinueHeuresUpsertGql = inject(
    FormationContinueHeuresUpsertGQL
  );
  private readonly updateInformationsRcpGql = inject(UpdateInformationsRcpGQL);
  private readonly updateInformationsRsacGql = inject(UpdateInformationsRsacGQL);
  private readonly updateInformationsKbisGql = inject(UpdateInformationsKbisGQL);
  private readonly updateInformationsAttVigGql = inject(UpdateInformationsAttVigGQL);
  private readonly updateInformationsCerfaGql = inject(UpdateInformationsCerfaGQL);
  private readonly updateInformationsNotifAffGql = inject(UpdateInformationsNotifAffGQL);
  private readonly datesDebutValiditeValideesGql = inject(
    DatesDebutValiditeValideesByDemandeValidationPjGQL
  );
  private readonly updateInformationsSyntUrssafGql = inject(
    UpdateInformationsSyntUrssafGQL
  );
  private readonly updateInformationsNotifDelaiUrssafGql = inject(
    UpdateInformationsNotifDelaiUrssafGQL
  );

  // Habilitation
  private readonly getDemandeHabilitationForControlGql = inject(
    GetDemandeHabilitationForControlGQL
  );

  public async getDemandeAffiliationForControle(
    publicId: string
  ): Promise<DemandeAffiliation> {
    const queryResult = await lastValueFrom(
      this.getDemandeAffiliationGql.fetch({
        variables: { publicId: publicId },
        ...{ fetchPolicy: 'network-only' },
      })
    );

    const result = DemandeAffiliationMapper.fromGraphQL(
      queryResult.data!.demandeAffiliationById as DemandeAffiliationGQL,
      true
    );

    return result;
  }

  public async getDemandeValidationPjForControle(
    publicId: string
  ): Promise<DemandeValidationPj> {
    const queryResult = await lastValueFrom(
      this.getDemandeValidationPjGql.fetch({
        variables: { publicId: publicId },
        ...{ fetchPolicy: 'network-only' },
      })
    );

    const result = DemandeValidationPjMapper.fromGraphQL(
      queryResult.data!.demandeValidationPjById as DemandeValidationPjGQL,
      true
    );

    await this.attachFormationContinueHeures(result);

    return result;
  }

  public async getDemandeHabilitationForControle(
    publicId: string
  ): Promise<DemandeHabilitation> {
    const queryResult = await lastValueFrom(
      this.getDemandeHabilitationForControlGql.fetch({
        variables: { publicId: publicId },
        ...{ fetchPolicy: 'network-only' },
      })
    );

    const result = DemandeHabilitationMapper.fromGraphQL(
      queryResult.data!.demandeHabilitationById as DemandeHabilitationGQL,
      true
    );

    for (const habDocument of result.habilitationDocuments ?? []) {
      if (habDocument.demandeValidationPj) {
        await this.attachFormationContinueHeures(habDocument.demandeValidationPj);
      }
    }

    return result;
  }

  override async editIdentite(publicId: string, patch: IdentitePatch): Promise<void> {
    const input: InformationsIdentiteUpdateCommandInput = {
      demandeAffiliationPublicId: publicId,
      civilite: CiviliteMapper.toGraphql(patch.civilite)!,
      nomNaissance: patch.nomNaissance,
      nomUsage: patch.nomUsage,
      prenom: patch.prenom,
      prenomsSecondaires: patch.prenomsSecondaires,
      dateNaissance: patch.dateNaissance.toISOString().split('T')[0],
      lieuNaissance: patch.lieuNaissance,
      departementNaissance: patch.departementNaissance,
      pays: patch.pays,
      nationalite: patch.nationalite,
      adresse: patch.adresse,
    };

    await lastValueFrom(
      this.updateInformationsIdentiteGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editCoordonnees(
    publicId: string,
    patch: CoordonneesPatch
  ): Promise<void> {
    const input: InformationsCoordonneesUpdateCommandInput = {
      demandeAffiliationPublicId: publicId,
      email: patch.email,
      emailFacturation: patch.emailFacturation,
      mobile: patch.mobile,
    };

    await lastValueFrom(
      this.updateInformationsCoordonneesGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editActiviteImmobiliere(
    publicId: string,
    patch: CarteTPatch
  ): Promise<void> {
    const input: InformationsCarteTUpdateCommandInput = {
      demandeAffiliationPublicId: publicId,
      civilite: CiviliteMapper.toGraphql(patch.civilite)!,
      nomUsage: patch.nomUsage,
      prenom: patch.prenom,
      prenomsSecondaires: patch.prenomsSecondaires,
      numeroCarteT: patch.numeroCarteT,
      cciDelivrance: patch.cciDelivrance,
      dateExpiration: patch.dateExpiration,
    };

    await lastValueFrom(
      this.updateInformationsCarteTGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editStatutJuridiqueSociete(
    publicId: string,
    patch: StatutJuridiqueSocietePatch
  ): Promise<void> {
    const input: InformationsSocieteUpdateCommandInput = {
      demandeAffiliationPublicId: publicId,
      formeJuridique: patch.formeJuridique,
      numeroImmatriculationRCS: patch.numeroImmatriculationRCS,
      raisonSociale: patch.raisonSociale,
      numeroSiret: patch.numeroSiret,
      numeroTVA: patch.numeroTVA,
      adresse: patch.adresse,
    };

    await lastValueFrom(
      this.updateInformationsSocieteGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editStatutJuridiqueProfessionLiberale(
    publicId: string,
    patch: StatutJuridiqueProfessionLiberalePatch
  ): Promise<void> {
    const input: InformationsProfessionLiberaleUpdateCommandInput = {
      demandeAffiliationPublicId: publicId,
      numeroSiren: patch.numeroSiren,
      numeroTVA: patch.numeroTVA ?? null,
    };

    await lastValueFrom(
      this.updateInformationsProfessionLiberaleGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editInformationsRcp(
    publicId: string,
    patch: PieceJustificativeRcpPatch
  ): Promise<void> {
    const input: InformationsUpdateRcpCommandInput = {
      demandeValidationPjPublicId: publicId,
      assureurId: patch.assureurId!,
      dateDebutValidite: patch.dateDebutValidite,
      dateFinValidite: patch.dateFinValidite,
      dateFinValiditeImmo: patch.dateFinValiditeImmo,
      rcpType: patch.rcpType!,
      statutsJuridiqueId: patch.statutsJuridiqueId,
    };

    await lastValueFrom(
      this.updateInformationsRcpGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editInformationsRsac(
    publicId: string,
    patch: PieceJustificativeRsac
  ): Promise<void> {
    const input: InformationsUpdateRsacCommandInput = {
      demandeValidationPjPublicId: publicId,
      dateDebutValidite: patch.dateDebutValidite,
      dateFinValidite: patch.dateFinValidite,
      voie: patch.voie,
      codePostal: patch.codePostal,
      ville: patch.ville,
      pays: patch.pays,
      nomCommercial: patch.nomCommercial,
      numeroSiret: patch.numeroSiret,
    };

    await lastValueFrom(
      this.updateInformationsRsacGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editInformationsKbis(
    publicId: string,
    patch: PieceJustificativeKbis
  ): Promise<void> {
    const input: InformationsUpdateKbisCommandInput = {
      demandeValidationPjPublicId: publicId,
      dateDebutValidite: patch.dateDebutValidite,
      dateFinValidite: patch.dateFinValidite,
      voie: patch.voie,
      codePostal: patch.codePostal,
      ville: patch.ville,
      pays: patch.pays,
      capitalSocial: patch.capitalSocial,
      formeJuridique: patch.formeJuridique,
      raisonSociale: patch.raisonSociale,
      villeRegistreCommerce: patch.villeRegistreCommerce,
      numeroSiret: patch.numeroSiret,
      numeroSiren: patch.numeroSiren,
      numeroTva: patch.numeroTva,
    };

    await lastValueFrom(
      this.updateInformationsKbisGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editInformationsAttVig(
    publicId: string,
    patch: PieceJustificativeAttVig
  ): Promise<void> {
    const input: InformationsUpdateAttVigCommandInput = {
      demandeValidationPjPublicId: publicId,
      dateDebutValidite: patch.dateDebutValidite,
      dateFinValidite: patch.dateFinValidite,
    };

    await lastValueFrom(
      this.updateInformationsAttVigGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editInformationsCerfa(
    publicId: string,
    patch: PieceJustificativeCerfa
  ): Promise<void> {
    const input: InformationsUpdateCerfaCommandInput = {
      demandeValidationPjPublicId: publicId,
      dateDebutValidite: patch.dateDebutValidite,
      dateFinValidite: patch.dateFinValidite,
    };

    await lastValueFrom(
      this.updateInformationsCerfaGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editInformationsNotifAff(
    publicId: string,
    patch: PieceJustificativeNotifAff
  ): Promise<void> {
    const input: InformationsUpdateNotifAffCommandInput = {
      demandeValidationPjPublicId: publicId,
      dateDebutValidite: patch.dateDebutValidite,
      dateFinValidite: patch.dateFinValidite,
    };

    await lastValueFrom(
      this.updateInformationsNotifAffGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async getDatesDebutValiditeValidees(publicId: string): Promise<string[]> {
    const response = await lastValueFrom(
      this.datesDebutValiditeValideesGql.fetch({
        variables: {
          demandeValidationPjPublicId: publicId,
        },
        ...{ fetchPolicy: 'network-only' },
      })
    );

    return response.data!.datesDebutValiditeValideesByDemandeValidationPj;
  }

  override async editInformationsSyntUrssaf(
    publicId: string,
    patch: PieceJustificativeSyntUrssaf
  ): Promise<void> {
    const input: InformationsUpdateSyntUrssafCommandInput = {
      demandeValidationPjPublicId: publicId,
      dateDebutValidite: patch.dateDebutValidite,
      dateFinValidite: patch.dateFinValidite,
    };

    await lastValueFrom(
      this.updateInformationsSyntUrssafGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  override async editInformationsNotifDelaiUrssaf(
    publicId: string,
    patch: PieceJustificativeNotifDelaiUrssaf
  ): Promise<void> {
    const input: InformationsUpdateNotifDelaiUrssafCommandInput = {
      demandeValidationPjPublicId: publicId,
      dateDebutValidite: patch.dateDebutValidite,
      dateFinValidite: patch.dateFinValidite,
    };

    await lastValueFrom(
      this.updateInformationsNotifDelaiUrssafGql.mutate({
        variables: {
          patch: input,
        },
      })
    );
  }

  public async saveConformite(
    demandeDocuments: DocumentModel[],
    publicId: string,
    demandeType: DemandeType
  ): Promise<void> {
    const input: DocumentConformiteUpdateInput[] = demandeDocuments!.map(
      d =>
        ({
          documentId: d.id,
          statut: DocumentStatutMapper.toGraphQl(d.statut),
          nonConformites:
            d.nonConformites?.map(
              nc =>
                ({
                  id: nc.id ? nc.id : undefined,
                  categorie: NonConformiteCategorieMapper.toGraphQl(nc.categorie),
                  commentaire: nc.commentaire,
                  opened: nc.opened,
                }) as NonConformiteUpdateInput
            ) ?? [],
        }) as DocumentConformiteUpdateInput
    );

    if (demandeType === 'affiliation') {
      await lastValueFrom(
        this.updateDocumentsConformiteGql.mutate({
          variables: {
            demandeAffiliationPublicId: publicId,
            documentsConformitesInput: input,
          },
        })
      );
    } else {
      await lastValueFrom(
        this.updateDocumentsConformiteValidationPjGql.mutate({
          variables: {
            demandeValidationPjPublicId: publicId,
            documentsConformitesInput: input,
          },
        })
      );

      await this.saveFormationContinueHeures(demandeDocuments, publicId);
    }
  }

  public async envoyerNonConformites(
    demandeDocuments: DocumentModel[],
    publicId: string,
    demandeType: DemandeType
  ): Promise<void> {
    await this.saveConformite(demandeDocuments, publicId, demandeType);

    if (demandeType === 'affiliation') {
      await lastValueFrom(
        this.nonConformitesEnvoiGql.mutate({
          variables: {
            demandeAffiliationPublicId: publicId,
          },
        })
      );
    } else if (demandeType === 'validationPj' || demandeType === 'habilitation') {
      await lastValueFrom(
        this.nonConformitesValidationPjEnvoiGql.mutate({
          variables: {
            demandeValidationPjPublicId: publicId,
          },
        })
      );
    }
  }

  public async envoyerMisesAJourDocumentsHabilitation(
    documents: DocumentModel[],
    demandeValidationPjPublicId: string
  ): Promise<void> {
    if (documents.length === 0) return;

    await this.saveConformite(documents, demandeValidationPjPublicId, 'habilitation');
    await lastValueFrom(
      this.nonConformitesValidationPjEnvoiGql.mutate({
        variables: {
          demandeValidationPjPublicId,
          documentIds: documents.map(document => document.id),
        },
      })
    );

    if (documents.every(document => document.statut === 'valide'))
      await lastValueFrom(
        this.validationDemandePjGql.mutate({
          variables: { publicId: demandeValidationPjPublicId },
        })
      );
  }

  public async envoyerEnSignature(demandeAffiliation: DemandeAffiliation): Promise<void> {
    await this.envoyerNonConformites(
      demandeAffiliation.documents!,
      demandeAffiliation.publicId!,
      'affiliation'
    );

    await lastValueFrom(
      this.contratCreateAndSignGql.mutate({
        variables: { publicId: demandeAffiliation.publicId! },
      })
    );
  }

  public async validerDemandePj(demandeValidationPj: DemandeValidationPj): Promise<void> {
    await this.envoyerNonConformites(
      demandeValidationPj.documents!,
      demandeValidationPj.publicId!,
      'validationPj'
    );

    await lastValueFrom(
      this.validationDemandePjGql.mutate({
        variables: { publicId: demandeValidationPj.publicId! },
      })
    );
  }

  public async envoyerEnSignatureValidationPj(
    demandeValidationPj: DemandeValidationPj
  ): Promise<void> {
    await lastValueFrom(
      this.demandeValidationPjContAgentCoEnvoyerEnSignatureGql.mutate({
        variables: { publicId: demandeValidationPj.publicId! },
      })
    );
  }

  private async attachFormationContinueHeures(
    demandeValidationPj: DemandeValidationPj
  ): Promise<void> {
    if (!demandeValidationPj.publicId || !demandeValidationPj.documents) return;

    const response = await lastValueFrom(
      this.formationContinueHeuresByDemandeValidationPjGql.fetch({
        variables: {
          demandeValidationPjPublicId: demandeValidationPj.publicId,
        },
        ...{ fetchPolicy: 'network-only' },
      })
    );

    for (const document of demandeValidationPj.documents) {
      document.formationContinueHeures = response
        .data!.formationContinueHeuresByDemandeValidationPj.filter(
          heures => heures.documentId === document.id
        )
        .map(heures => ({
          id: heures.id,
          documentId: heures.documentId,
          demandeValidationPjId: heures.demandeValidationPjId,
          formationType: FormationContinueMapper.fromGraphQl(heures.formationType),
          year: heures.year,
          hours: heures.hours,
        }));
    }
  }

  public async getFormationContinueSummary(
    codeConsultant: string,
    year: number
  ): Promise<FormationContinueSummary[]> {
    const response = await lastValueFrom(
      this.formationContinueSummaryGql.fetch({
        variables: {
          codeConsultant,
          year,
        },
        ...{ fetchPolicy: 'network-only' },
      })
    );

    return response.data!.formationContinueSummary.map(summary => ({
      formationType: FormationContinueMapper.fromGraphQl(summary.formationType),
      year: summary.year,
      requiredHours: summary.requiredHours,
      completedHours: summary.completedHours,
      remainingHours: summary.remainingHours,
      hasRequiredHoursForHabilitation: summary.hasRequiredHoursForHabilitation,
    }));
  }

  private async saveFormationContinueHeures(
    demandeDocuments: DocumentModel[],
    demandeValidationPjPublicId: string
  ): Promise<void> {
    const documentsToSave = demandeDocuments.filter(
      document =>
        this.isFormationContinueHoursDocument(document) &&
        document.formationContinueHeures?.some(heures => {
          const hours = FormationContinueMapper.parseHours(heures.hours);
          return !!hours && hours > 0 && !!heures.year;
        })
    );

    for (const document of documentsToSave) {
      const heures = document.formationContinueHeures![0];
      const hours = FormationContinueMapper.parseHours(heures.hours);
      const formationType = FormationContinueMapper.toGraphQl(heures.formationType);

      if (!hours || !heures.year) continue;

      const response = await lastValueFrom(
        this.formationContinueHeuresUpsertGql.mutate({
          variables: {
            demandeValidationPjPublicId,
            documentId: document.id,
            formationType,
            year: heures.year,
            hours,
          },
        })
      );

      const saved =
        response.data?.formationContinueHeuresUpsert.consultantFormationContinueHeures;
      if (saved) {
        document.formationContinueHeures = [
          {
            id: saved.id,
            documentId: saved.documentId,
            demandeValidationPjId: saved.demandeValidationPjId,
            formationType: FormationContinueMapper.fromGraphQl(saved.formationType),
            year: saved.year,
            hours: saved.hours,
          },
        ];
      }
    }
  }

  private isFormationContinueDocument(document: DocumentModel): boolean {
    return (
      document.documentType === 'formation_continue_immo' ||
      document.documentType === 'formation_continue_assu' ||
      document.documentType === 'formation_continue_financement'
    );
  }

  private isFormationContinueHoursDocument(document: DocumentModel): boolean {
    return (
      document.documentType === 'formation_continue_assu' ||
      document.documentType === 'formation_continue_financement'
    );
  }
}
